import { LayoutDashboard, FolderKanban, Bell, LogOut, Settings } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';

import { useAuth } from '../../lib/auth-context';
import { cn } from '../../lib/cn';
import { Avatar, AvatarFallback, initialsFor } from '../ui/avatar';
import { Button } from '../ui/button';
import { LanguageSwitcher } from '../ui/language-switcher';

interface NavItem {
  to: string;
  labelKey: string;
  icon: ReactNode;
}

const NAV: NavItem[] = [
  { to: '/dashboard', labelKey: 'nav.dashboard', icon: <LayoutDashboard className="h-4 w-4" /> },
  { to: '/projects', labelKey: 'nav.projects', icon: <FolderKanban className="h-4 w-4" /> },
  { to: '/notifications', labelKey: 'nav.notifications', icon: <Bell className="h-4 w-4" /> },
];

export function AppShell({ children }: { children: ReactNode }): JSX.Element {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="flex h-full app-bg">
      <aside className="hidden md:flex w-60 flex-col border-r border-surface-200 bg-white/95 backdrop-blur">
        <div className="flex h-14 items-center px-4 border-b border-surface-200/70">
          <span className="text-lg font-semibold tracking-tight text-brand-700">
            GOPINHOS
          </span>
          <span className="ml-2 inline-flex items-center rounded-full bg-accent-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent-700">
            beta
          </span>
        </div>
        <nav className="flex-1 space-y-0.5 px-2">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium',
                  isActive ? 'bg-brand-50 text-brand-700' : 'text-surface-600 hover:bg-surface-100',
                )
              }
            >
              {item.icon}
              {t(item.labelKey)}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-surface-200 p-3">
          <div className="flex items-center gap-2 px-2 py-1.5">
            <Avatar>
              <AvatarFallback>{initialsFor(user?.fullName ?? user?.email ?? '?')}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{user?.fullName ?? user?.email}</p>
              <p className="truncate text-xs text-surface-500">{user?.role}</p>
            </div>
          </div>
          <NavLink
            to="/settings/notifications"
            className={({ isActive }) =>
              cn(
                'mt-1 flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium',
                isActive ? 'bg-brand-50 text-brand-700' : 'text-surface-600 hover:bg-surface-100',
              )
            }
          >
            <Settings className="h-4 w-4" /> {t('nav.settings')}
          </NavLink>
          <Button
            variant="ghost"
            size="sm"
            className="mt-1 w-full justify-start"
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
          >
            <LogOut className="h-4 w-4" /> {t('common.signOut')}
          </Button>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto">
        {/* Top bar — desktop + mobile. Holds the language switcher (top-right
            on every screen size) plus the brand mark on mobile. */}
        <header className="sticky top-0 z-20 flex h-12 items-center justify-between border-b border-surface-200 bg-white/90 px-4 backdrop-blur md:justify-end md:h-12 md:px-6">
          <span className="text-base font-semibold text-brand-700 md:hidden">GOPINHOS</span>
          <LanguageSwitcher />
        </header>
        <div className="p-4 md:p-6 pb-24 md:pb-6">{children}</div>
        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-surface-200 bg-white grid grid-cols-3">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'flex flex-col items-center justify-center gap-1 py-2 text-xs font-medium',
                  isActive ? 'text-brand-700' : 'text-surface-500',
                )
              }
            >
              {item.icon}
              {t(item.labelKey)}
            </NavLink>
          ))}
        </nav>
      </main>
    </div>
  );
}
